import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { selectIsLoggedIn, selectSession, SessionActions } from '@state/session';
import { of } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { RestService } from 'cargo-chief-commons';
import { UrlService } from '@service/url/url.service';

@Injectable({
	providedIn: 'root',
})
export class AuthGuardV2Effects {
	constructor(
		private store: Store,
		private actions$: Actions<any>,
		private router: Router,
		private restService: RestService,
		private urlService: UrlService,
		private activatedRoute: ActivatedRoute
	) {}

	storageUpdated$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SessionActions.storage.update),
				switchMap(() => this.store.select(selectSession).pipe(take(1))),
				tap(async (session) => {
					if (session?.loggedIn === false) {
						await this.router.navigate(['/login']);
					}
				})
			),
		{ dispatch: false }
	);

	/**
	 * Responsible for automatically logging user in.
	 * Expected functionality:
	 * 	- if user has session data redirect them to their landingPage
	 * 	- If user doesn't have session data run validation action with redirection payload
	 */
	navigated$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ROUTER_NAVIGATED),
			filter((action) => action.payload.routerState.url === '/login'),
			switchMap(() => this.store.select(selectIsLoggedIn).pipe(take(1))),
			filter((loggedIn) => loggedIn),
			switchMap(() =>
				of(
					SessionActions.validate.start({
						payload: { route: this.activatedRoute.snapshot, redirect: true },
					})
				)
			)
		)
	);

	validateStarted$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SessionActions.validate.start),
			switchMap(({ payload }) =>
				this.restService.GET(this.urlService.loginSetupUrl()).pipe(
					map(() => SessionActions.login.setup.start({ payload: { redirect: payload.redirect } })),
					catchError((e) => {
						return of(SessionActions.validate.fail({ payload: { route: payload.route } }));
					})
				)
			)
		)
	);

	validateFail$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SessionActions.validate.fail),
				tap(({ payload }) => {
					const url = JSON.stringify(this.router.url);
					let redirectUrl = payload.route.routeConfig.path,
						queryParams = {};
					if (payload?.route['_routerState']?.url) {
						redirectUrl = payload.route['_routerState'].url;
					}
					if (redirectUrl) {
						queryParams['path'] = redirectUrl;
					}
					this.router.navigate(['/login'], {
						queryParams: {
							path: redirectUrl,
							...payload.route.queryParams,
						},
					});
				})
			),
		{ dispatch: false }
	);
}
